import "@/styles/orbit-v2.css";
import { NavLink } from "react-router-dom";

export const OBJECT_COLORS = {
  activite: "var(--orbit-blue)", mission: "var(--orbit-orange)", plan: "var(--orbit-indigo)",
  alerte: "var(--orbit-coral)", sondage: "var(--orbit-violet)", ia: "var(--orbit-violet)",
  green: "var(--orbit-green)", red: "var(--orbit-red)", amber: "var(--orbit-amber)", blue: "var(--orbit-blue)", muted: "var(--orbit-muted)",
};

export function OrbitPageHeader({ kicker, title, description, actions, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={testid}>
      <div className="min-w-0">
        {kicker && <div className="o-label mb-1">{kicker}</div>}
        <h1 className="o-page-title">{title}</h1>
        {description && <p className="o-body o-muted mt-1">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function OrbitSectionNav({ items, testidPrefix = "nav" }) {
  return (
    <nav className="o-nav" data-testid={`${testidPrefix}-section-nav`}>
      {items.map(it => {
        const Icon = it.icon;
        return (
          <NavLink key={it.to} to={it.to} className="o-nav-item" data-testid={`${testidPrefix}-${it.to}`}>
            {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />}{it.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function OrbitMetricRail({ children, cols, testid }) {
  return <div className="o-rail" style={{ gridTemplateColumns: `repeat(${cols || (Array.isArray(children) ? children.filter(Boolean).length : 1)}, minmax(0,1fr))` }} data-testid={testid}>{children}</div>;
}

export function OrbitMetric({ label, value, sub, tone = "muted", onClick, testid }) {
  const color = { up: "var(--orbit-green)", down: "var(--orbit-red)", warn: "var(--orbit-amber)", muted: "var(--orbit-muted)" }[tone] || "var(--orbit-muted)";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className="o-rail-item" onClick={onClick} data-testid={testid}>
      <span className="o-label">{label}</span>
      <span className="o-kpi">{value}</span>
      {sub && <span className="text-[11.5px] font-semibold" style={{ color }}>{sub}</span>}
    </Tag>
  );
}

export function OrbitPanel({ title, hint, action, children, className = "", testid }) {
  return (
    <section className={`o-panel ${className}`} data-testid={testid}>
      {(title || action) && (
        <header className="o-panel-head">
          <h3 className="o-module-title">{title}{hint && <span className="o-muted font-medium"> — {hint}</span>}</h3>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function OrbitLink({ onClick, children, testid }) {
  return <button onClick={onClick} className="o-link" data-testid={testid}>{children}</button>;
}

export function OrbitStatus({ label, color = "muted", testid }) {
  return <span className="o-status" style={{ "--dot": OBJECT_COLORS[color] || color }} data-testid={testid}><i />{label}</span>;
}

export function OrbitProgressNodes({ steps = 4, done = 0, color = "blue", pct, testid }) {
  const c = OBJECT_COLORS[color] || color;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="o-prog flex-1" style={{ "--c": c }}>
        {Array.from({ length: steps }).map((_, i) => (
          <span key={i} className="contents">
            {i > 0 && <span className={`o-prog-seg ${i < done ? "on" : ""}`} />}
            <span className={`o-prog-node ${i < done ? "on" : ""}`} />
          </span>
        ))}
      </div>
      {pct != null && <span className="text-[11px] font-bold tabular-nums w-9 text-right">{pct}%</span>}
    </div>
  );
}

export function OrbitBar({ pct = 0, color = "blue", className = "" }) {
  return <div className={`o-bar ${className}`} style={{ "--c": OBJECT_COLORS[color] || color }}><i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>;
}

export function OrbitTimeline({ items, testid }) {
  return (
    <ol className="o-tl" data-testid={testid}>
      {items.map((it, i) => (
        <li key={it.key || i} className="relative py-2" data-testid={it.testid}>
          <span className={`o-tl-node ${it.filled !== false ? "fill" : ""}`} style={{ "--c": OBJECT_COLORS[it.color] || it.color || "var(--orbit-blue)" }} />
          {it.content}
        </li>
      ))}
    </ol>
  );
}

export function OrbitInsight({ title, items, action, testid }) {
  return (
    <section className="o-insight p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[13px] leading-none" style={{ color: "var(--orbit-violet)" }}>✦</span>
        <span className="o-label" style={{ color: "var(--orbit-violet)" }}>{title}</span>
      </div>
      <div className="space-y-3">{items}</div>
      {action && <div className="mt-3 flex items-center gap-4">{action}</div>}
    </section>
  );
}

export function OrbitEmptyState({ title, hint, testid }) {
  return (
    <div className="o-empty" data-testid={testid}>
      <span className="ring" />
      <div className="text-[12.5px] font-bold">{title}</div>
      {hint && <div className="text-[11.5px] o-muted">{hint}</div>}
    </div>
  );
}
